'use client';

import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import Script from 'next/script';

import { createClient } from '@/lib/supabase/client';

// ── Constants ─────────────────────────────────────────────────────────────────
const FREE_ACCESS_EMAILS = [
  'mohamedshahid1530@gmail.com',
  'mohammedshahid03@gmail.com',
];

// ── Types ────────────────────────────────────────────────────────────────────
type AuthTab = 'signin' | 'signup' | 'forgot';

interface SignInEmailForm {
  email: string;
  password: string;
}

interface SignUpEmailForm {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface ForgotForm {
  email: string;
}

interface Plan {
  id: string;
  name: string;
  price: number;
  period: string;
  features: string[];
  badge?: string;
  color: string;
  highlight: boolean;
}

const PLANS: Plan[] = [
  {
    id: 'basic',
    name: 'Basic',
    price: 50,
    period: '/year',
    features: [
      '₹50 / Year',
      'Includes up to 1000 transit searches annually',
      'Best for occasional users',
    ],
    color: 'from-blue-500 to-blue-600',
    highlight: false,
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 100,
    period: '/year',
    features: [
      '₹100 / Year',
      'Includes up to 3000 transit searches annually',
      'Best for frequent users',
    ],
    badge: 'Recommended',
    color: 'from-amber-500 to-orange-500',
    highlight: true,
  },
];

// ── Eye Toggle ───────────────────────────────────────────────────────────────
function EyeIcon({ visible }: { visible: boolean }) {
  if (visible) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    );
  }
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );
}

// ── Input Field ───────────────────────────────────────────────────────────────
function AuthInput({
  label,
  type,
  placeholder,
  error,
  showToggle,
  visible,
  onToggle,
  registration,
  prefix
}: {
  label: string;
  type: string;
  placeholder: string;
  error?: string;
  showToggle?: boolean;
  visible?: boolean;
  onToggle?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registration: any;
  prefix?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-semibold text-gray-700">{label}</label>
      <div className="relative flex">
        {prefix && (
          <span className="flex items-center px-3 rounded-l-xl bg-gray-100 border border-r-0 border-gray-200 text-gray-500 text-sm font-medium select-none">
            {prefix}
          </span>
        )}
        <input
          {...registration}
          type={showToggle && visible ? 'text' : type}
          placeholder={placeholder}
          className={`w-full px-4 py-3 bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all duration-200 pr-11 ${
            prefix ? 'rounded-r-xl' : 'rounded-xl'
          }`}
        />
        {showToggle && (
          <button
            type="button"
            onClick={onToggle}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors"
          >
            <EyeIcon visible={!!visible} />
          </button>
        )}
      </div>
      {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}
    </div>
  );
}

// ── Helper: Save pending subscription after auth ──────────────────────────────
async function savePendingSubscription(userId: string, accessToken: string) {
  try {
    const raw = sessionStorage.getItem('pending_subscription');
    if (!raw) return;
    const pending = JSON.parse(raw);
    if (!pending?.razorpay_payment_id) return;

    const res = await fetch('/api/razorpay/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        razorpay_order_id: pending.razorpay_order_id,
        razorpay_payment_id: pending.razorpay_payment_id,
        razorpay_signature: pending.razorpay_signature,
        planId: pending.planId,
        userId,
        accessToken,
      }),
    });
    const data = await res.json();
    if (data.success) {
      sessionStorage.removeItem('pending_subscription');
    }
  } catch {
    // Non-critical — subscription already verified, just linking failed
  }
}

// ── Sign In Form ──────────────────────────────────────────────────────────────
function SignInForm({ onForgot, paymentDone, isFreeEmail }: { onForgot: () => void; paymentDone: boolean; isFreeEmail: boolean }) {
  const router = useRouter();
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const isUnlocked = paymentDone || isFreeEmail;

  const { register, handleSubmit, formState: { errors }, setError } = useForm<SignInEmailForm>();

  const onSubmit = async (data: SignInEmailForm) => {
    setLoading(true);
    try {
      const supabase = createClient();
      const { data: authData, error } = await supabase.auth.signInWithPassword({ email: data.email, password: data.password });
      if (error) throw error;
      // Link any pending subscription payment to this user
      if (authData.session) {
        await savePendingSubscription(authData.session.user.id, authData.session.access_token);
      }
      toast.success('Welcome back! 🎉');
      router.push('/profile');
    } catch (err: any) {
      setError('email', { message: err.message || 'Invalid credentials. Please check your email and password.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      {!isUnlocked && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
          <span className="text-amber-500 text-lg">🔒</span>
          <p className="text-xs text-amber-700 font-medium">Select a plan and complete payment to sign in.</p>
        </div>
      )}

      {isUnlocked && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2.5">
          <span className="text-green-500 text-lg">✅</span>
          <p className="text-xs text-green-700 font-medium">
            {isFreeEmail ? 'Free access granted. Sign in below.' : 'Payment successful! You can now sign in.'}
          </p>
        </div>
      )}

      <div className={!isUnlocked ? 'opacity-40 pointer-events-none select-none' : ''}>
        <div className="flex flex-col gap-4">
          <AuthInput
            label="Email Address"
            type="email"
            placeholder="you@example.com"
            error={errors.email?.message}
            registration={register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' }
            })}
          />
          <AuthInput
            label="Password"
            type="password"
            placeholder="Your password"
            error={errors.password?.message}
            showToggle
            visible={showPwd}
            onToggle={() => setShowPwd((v) => !v)}
            registration={register('password', { required: 'Password is required' })}
          />
          <div className="flex justify-end">
            <button type="button" onClick={onForgot} className="text-xs text-primary hover:text-orange-500 font-medium transition-colors">Forgot Password?</button>
          </div>
          <button
            type="submit"
            disabled={loading || !isUnlocked}
            className="w-full py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-orange-500 active:scale-95 transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2 shadow-saffron"
          >
            {loading ? <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Signing In...</> : 'Sign In to Chennai'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ── Sign Up Form ──────────────────────────────────────────────────────────────
function SignUpForm({ onSuccess, paymentDone, isFreeEmail }: { onSuccess: () => void; paymentDone: boolean; isFreeEmail: boolean }) {
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const isUnlocked = paymentDone || isFreeEmail;

  const { register, handleSubmit, watch, formState: { errors } } = useForm<SignUpEmailForm>();
  const pwd = watch('password');

  const onSubmit = async (data: SignUpEmailForm) => {
    setLoading(true); setAuthError('');
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: { data: { full_name: data.fullName }, emailRedirectTo: `${window.location.origin}/auth/callback?next=/explorer` },
      });
      if (error) throw error;
      onSuccess();
    } catch (err: any) {
      setAuthError(err.message || 'Sign up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      {!isUnlocked && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
          <span className="text-amber-500 text-lg">🔒</span>
          <p className="text-xs text-amber-700 font-medium">Select a plan and complete payment to sign up.</p>
        </div>
      )}

      {isUnlocked && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2.5">
          <span className="text-green-500 text-lg">✅</span>
          <p className="text-xs text-green-700 font-medium">
            {isFreeEmail ? 'Free access granted. Sign up below.' : 'Payment successful! You can now sign up.'}
          </p>
        </div>
      )}

      <div className={!isUnlocked ? 'opacity-40 pointer-events-none select-none' : ''}>
        <div className="flex flex-col gap-4">
          <AuthInput label="Full Name" type="text" placeholder="Arjun Krishnan" error={errors.fullName?.message} registration={register('fullName', { required: 'Full name is required' })} />
          <AuthInput label="Email Address" type="email" placeholder="you@example.com" error={errors.email?.message} registration={register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' } })} />
          <AuthInput label="Password" type="password" placeholder="Create a strong password" error={errors.password?.message} showToggle visible={showPwd} onToggle={() => setShowPwd((v) => !v)} registration={register('password', { required: 'Password is required', minLength: { value: 8, message: 'Minimum 8 characters' } })} />
          <AuthInput label="Confirm Password" type="password" placeholder="Repeat your password" error={errors.confirmPassword?.message} showToggle visible={showConfirm} onToggle={() => setShowConfirm((v) => !v)} registration={register('confirmPassword', { required: 'Please confirm your password', validate: (v) => v === pwd || 'Passwords do not match' })} />
          {authError && <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{authError}</p>}
          <button type="submit" disabled={loading || !isUnlocked} className="w-full py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-orange-500 active:scale-95 transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2 shadow-saffron">
            {loading ? <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Creating Account...</> : 'Create My Account'}
          </button>
          <p className="text-xs text-gray-400 text-center">By signing up you agree to our <span className="text-primary cursor-pointer hover:underline">Terms</span> & <span className="text-primary cursor-pointer hover:underline">Privacy Policy</span></p>
        </div>
      </div>
    </form>
  );
}

// ── Forgot Password Form ──────────────────────────────────────────────────────
function ForgotForm({ onBack }: { onBack: () => void }) {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const { register, handleSubmit, formState: { errors } } = useForm<ForgotForm>();

  const onSubmit = async (data: ForgotForm) => {
    setLoading(true); setAuthError('');
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/auth/callback?next=/signin`,
      });
      if (error) throw error;
      setSent(true);
    } catch (err: any) {
      setAuthError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="flex flex-col items-center gap-4 py-4">
        <div className="w-16 h-16 rounded-full bg-green-500/20 border border-green-400/30 flex items-center justify-center text-3xl">✉️</div>
        <div className="text-center">
          <p className="text-gray-900 font-semibold text-lg">Check Your Inbox</p>
          <p className="text-gray-500 text-sm mt-1">A password reset link has been sent to your email address.</p>
        </div>
        <button onClick={onBack} className="text-sm text-primary hover:underline font-medium">← Back to Sign In</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <p className="text-gray-600 text-sm">Enter your email and we'll send you a reset link.</p>
      <AuthInput label="Email Address" type="email" placeholder="you@example.com" error={errors.email?.message} registration={register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' } })} />
      {authError && <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{authError}</p>}
      <button type="submit" disabled={loading} className="w-full py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-orange-500 active:scale-95 transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2">
        {loading ? <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Sending...</> : 'Send Reset Link'}
      </button>
      <button type="button" onClick={onBack} className="text-sm text-gray-500 hover:text-gray-800 transition-colors text-center">← Back to Sign In</button>
    </form>
  );
}

// ── Email Sent Success ────────────────────────────────────────────────────────
function EmailSentSuccess({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex flex-col items-center gap-5 py-4">
      <div className="w-20 h-20 rounded-full bg-blue-500/20 border-2 border-blue-400/40 flex items-center justify-center text-4xl">📧</div>
      <div className="text-center">
        <p className="text-gray-900 font-bold text-xl">Verify Your Email</p>
        <p className="text-gray-500 text-sm mt-2">A verification email has been sent. Please check your inbox and click the link to activate your account.</p>
      </div>
      <button onClick={onBack} className="w-full py-3 rounded-xl bg-gray-100 border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-200 transition-all">← Back to Sign In</button>
    </div>
  );
}

// ── Subscription Status Card ──────────────────────────────────────────────────
interface SubscriptionStatus {
  planName: string;
  remainingTransits: number;
  totalTransits: number;
  expiryDate: string;
  isActive: boolean;
}

function SubscriptionStatusCard({ status }: { status: SubscriptionStatus }) {
  const usedTransits = status.totalTransits - status.remainingTransits;
  const usagePercent = Math.min(100, Math.round((usedTransits / status.totalTransits) * 100));
  const expiry = new Date(status.expiryDate);
  const formattedExpiry = `${expiry.getDate().toString().padStart(2, '0')}/${(expiry.getMonth() + 1).toString().padStart(2, '0')}/${expiry.getFullYear()}`;

  return (
    <div className="bg-white/15 backdrop-blur-sm rounded-2xl border border-white/30 p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-bold text-sm">Your Subscription</h3>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${status.isActive ? 'bg-green-400/30 text-green-200 border border-green-400/40' : 'bg-red-400/30 text-red-200 border border-red-400/40'}`}>
          {status.isActive ? '● Active' : '● Expired'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="bg-white/10 rounded-xl p-2.5">
          <p className="text-white/60 text-xs mb-0.5">Active Plan</p>
          <p className="text-white font-bold text-sm">{status.planName}</p>
        </div>
        <div className="bg-white/10 rounded-xl p-2.5">
          <p className="text-white/60 text-xs mb-0.5">Expiry Date</p>
          <p className="text-white font-bold text-sm">{formattedExpiry}</p>
        </div>
      </div>

      {/* Transit Usage */}
      <div className="bg-white/10 rounded-xl p-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-white/60 text-xs">Transit Usage</p>
          <p className="text-white text-xs font-semibold">
            <span className="text-white font-bold">{status.remainingTransits.toLocaleString()}</span>
            <span className="text-white/60"> / {status.totalTransits.toLocaleString()} remaining</span>
          </p>
        </div>
        {/* Progress Bar */}
        <div className="w-full bg-white/20 rounded-full h-2.5 overflow-hidden">
          <div
            className={`h-2.5 rounded-full transition-all duration-500 ${usagePercent > 80 ? 'bg-red-400' : usagePercent > 50 ? 'bg-amber-400' : 'bg-green-400'}`}
            style={{ width: `${usagePercent}%` }}
          />
        </div>
        <p className="text-white/50 text-xs mt-1">{usagePercent}% used · {usedTransits.toLocaleString()} transits consumed</p>
      </div>
    </div>
  );
}

// ── Subscription Plans Panel ──────────────────────────────────────────────────
function SubscriptionPanel({
  selectedPlan,
  onSelectPlan,
  onPaymentSuccess,
  scriptLoaded,
  paymentDone,
  userId,
}: {
  selectedPlan: Plan | null;
  onSelectPlan: (plan: Plan) => void;
  onPaymentSuccess: (planId: string) => void;
  scriptLoaded: boolean;
  paymentDone: boolean;
  userId: string | null;
}) {
  const [payingPlanId, setPayingPlanId] = useState<string | null>(null);
  const [payError, setPayError] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);

  // Fetch subscription status for logged-in users
  useEffect(() => {
    const fetchStatus = async () => {
      if (!userId) return;
      try {
        const supabase = createClient();
        const now = new Date().toISOString();
        const { data: sub } = await supabase
          .from('user_subscriptions')
          .select('plan_name, expires_at, is_active, plan_id')
          .eq('user_id', userId)
          .eq('is_active', true)
          .gt('expires_at', now)
          .order('expires_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (sub) {
          const totalTransits = sub.plan_id === 'premium' ? 3000 : 1000;
          setSubscriptionStatus({
            planName: sub.plan_name,
            remainingTransits: totalTransits, // Full transits — usage tracking can be extended later
            totalTransits,
            expiryDate: sub.expires_at,
            isActive: sub.is_active,
          });
        }
      } catch {
        // Non-critical
      }
    };
    fetchStatus();
  }, [userId, paymentDone]);

  const handlePay = async (plan: Plan) => {
    if (!scriptLoaded) { setPayError('Payment gateway is loading. Please wait.'); return; }
    setPayError('');
    setPayingPlanId(plan.id);

    try {
      const res = await fetch('/api/razorpay/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: plan.price, planName: plan.name }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to create order');

      const options = {
        key: data.keyId,
        amount: data.amount,
        currency: data.currency,
        name: 'Explore Chennai',
        description: `${plan.name} Subscription`,
        order_id: data.orderId,
        handler: async (response: any) => {
          try {
            const supabase = createClient();
            const { data: { session } } = await supabase.auth.getSession();
            const accessToken = session?.access_token ?? null;
            const resolvedUserId = session?.user?.id ?? userId;

            const verifyRes = await fetch('/api/razorpay/verify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                ...response,
                planId: plan.id,
                userId: resolvedUserId,
                accessToken,
              }),
            });
            const verifyData = await verifyRes.json();
            if (verifyData.success) {
              if (!resolvedUserId || !accessToken) {
                try {
                  sessionStorage.setItem('pending_subscription', JSON.stringify({
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                    planId: plan.id,
                  }));
                } catch {}
              }
              toast.success(`🎉 ${plan.name} plan activated!`);
              onPaymentSuccess(plan.id);
            } else {
              setPayError('Payment verification failed. Please contact support.');
            }
          } catch {
            setPayError('Verification error. Please try again.');
          } finally {
            setPayingPlanId(null);
          }
        },
        modal: {
          ondismiss: () => setPayingPlanId(null),
        },
        prefill: { name: '', email: '', contact: '' },
        theme: { color: '#F59E0B' },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.on('payment.failed', (res: any) => {
        setPayError(res.error?.description || 'Payment failed. Please try again.');
        setPayingPlanId(null);
      });
      rzp.open();
    } catch (err: any) {
      setPayError(err.message || 'Something went wrong. Please try again.');
      setPayingPlanId(null);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-2xl">🚌</span>
          <h2 className="text-xl font-extrabold text-white drop-shadow">Choose Your Plan</h2>
        </div>
        <p className="text-white/80 text-sm font-medium drop-shadow">Unlock full access to Explore Chennai</p>
      </div>

      {/* Current Subscription Status */}
      {subscriptionStatus && <SubscriptionStatusCard status={subscriptionStatus} />}

      {/* Plans */}
      <div className="flex flex-col gap-3 flex-1">
        {PLANS.map((plan) => {
          const isSelected = selectedPlan?.id === plan.id;
          const isPaying = payingPlanId === plan.id;
          const transitLimit = plan.id === 'premium' ? '3,000 Transit searches / year' : '1,000 Transit searches / year';
          const validity = '1 Year';

          return (
            <div
              key={plan.id}
              onClick={() => !paymentDone && onSelectPlan(plan)}
              className={`relative rounded-2xl border-2 cursor-pointer transition-all duration-200 overflow-hidden shadow-lg ${
                plan.highlight
                  ? isSelected
                    ? 'border-amber-300 shadow-amber-400/30 scale-[1.02]'
                    : 'border-amber-300/70 hover:border-amber-300'
                  : isSelected
                    ? 'border-white shadow-white/20 scale-[1.02]'
                    : 'border-white/30 hover:border-white/60'
              } ${paymentDone ? 'cursor-default' : ''}`}
            >
              {/* Background gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${plan.color} opacity-95`} />

              {/* Recommended Badge */}
              {plan.badge && (
                <div className="absolute top-0 right-0 z-10">
                  <div className="bg-amber-300 text-amber-900 text-xs font-extrabold px-3 py-1 rounded-bl-xl rounded-tr-2xl shadow-md tracking-wide">
                    ⭐ {plan.badge}
                  </div>
                </div>
              )}

              <div className="relative z-10 p-4">
                {/* Plan Header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-white font-extrabold text-lg leading-tight">{plan.name}</p>
                    <div className="flex items-baseline gap-1 mt-0.5">
                      <span className="text-white text-2xl font-extrabold">₹{plan.price}</span>
                      <span className="text-white/80 text-sm font-medium">{plan.period}</span>
                    </div>
                  </div>
                  {/* Selection indicator */}
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all ${isSelected ? 'border-white bg-white' : 'border-white/60 bg-white/10'}`}>
                    {isSelected && <div className={`w-3 h-3 rounded-full bg-gradient-to-br ${plan.color}`} />}
                  </div>
                </div>

                {/* Plan Details Row */}
                <div className="flex gap-2 mb-3">
                  <div className="flex items-center gap-1.5 bg-white/20 rounded-lg px-2.5 py-1.5 flex-1">
                    <svg className="w-3.5 h-3.5 text-white/80 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <p className="text-white/60 text-xs leading-none">Validity</p>
                      <p className="text-white text-xs font-bold">{validity}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white/20 rounded-lg px-2.5 py-1.5 flex-1">
                    <svg className="w-3.5 h-3.5 text-white/80 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    <div>
                      <p className="text-white/60 text-xs leading-none">Transits</p>
                      <p className="text-white text-xs font-bold">{transitLimit}</p>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <ul className="flex flex-col gap-1.5 mb-3">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-white/90 text-xs">
                      <div className="w-4 h-4 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>

                {/* Subscribe Now button — always visible, not just when selected */}
                {!paymentDone && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onSelectPlan(plan); handlePay(plan); }}
                    disabled={isPaying || !scriptLoaded}
                    className={`w-full py-2.5 rounded-xl font-bold text-sm active:scale-95 transition-all duration-150 disabled:opacity-60 flex items-center justify-center gap-2 shadow-md ${
                      plan.highlight
                        ? 'bg-white text-amber-700 hover:bg-amber-50' :'bg-white text-blue-700 hover:bg-blue-50'
                    }`}
                  >
                    {isPaying ? (
                      <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Processing...</>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        Subscribe Now
                      </>
                    )}
                  </button>
                )}

                {paymentDone && isSelected && (
                  <div className="w-full py-2.5 rounded-xl bg-white/20 text-white font-bold text-sm flex items-center justify-center gap-2">
                    ✅ Subscribed — Access Unlocked
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {payError && (
        <div className="mt-3 bg-red-500/20 border border-red-400/40 rounded-xl px-3 py-2">
          <p className="text-red-100 text-xs font-medium">{payError}</p>
        </div>
      )}

      <p className="text-white/60 text-xs text-center mt-3 drop-shadow">
        🔒 Secured by Razorpay · Annual billing
      </p>
    </div>
  );
}

// ── Terms & Conditions Modal ──────────────────────────────────────────────────
function TermsModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-amber-50">
          <div className="flex items-center gap-2">
            <span className="text-xl">📋</span>
            <h2 className="text-base font-extrabold text-gray-800">Terms &amp; Conditions</h2>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-5 py-4 flex-1">
          <p className="text-xs text-gray-500 mb-4 font-medium">Please read these terms carefully before using Explore Chennai.</p>
          <ol className="flex flex-col gap-4">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-extrabold flex items-center justify-center mt-0.5">1</span>
              <p className="text-sm text-gray-700 leading-relaxed">
                The transit routes, both bus and metro, are provided to help travellers access the city hassle free.
              </p>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-extrabold flex items-center justify-center mt-0.5">2</span>
              <p className="text-sm text-gray-700 leading-relaxed">
                The developer and the team will not be responsible for any glitches or incorrect information provided through the application.
              </p>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-extrabold flex items-center justify-center mt-0.5">3</span>
              <p className="text-sm text-gray-700 leading-relaxed">
                The application does not issue any transit passes and it serves like a city mapper only.
              </p>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-extrabold flex items-center justify-center mt-0.5">4</span>
              <p className="text-sm text-gray-700 leading-relaxed">
                The application relies on open transit feeds only (delays are normal).
              </p>
            </li>
          </ol>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-xl bg-primary text-white font-bold text-sm hover:bg-orange-500 active:scale-95 transition-all duration-150 shadow-sm"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Auth Page ─────────────────────────────────────────────────────────────
export default function AuthPage() {
  const [tab, setTab] = useState<AuthTab>('signin');
  const [signupState, setSignupState] = useState<'form' | 'email-sent'>('form');
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [paymentDone, setPaymentDone] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [emailForFreeCheck, setEmailForFreeCheck] = useState('');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [showTerms, setShowTerms] = useState(false);

  // Detect free-access email as user types — we watch the email field via a shared state
  const isFreeEmail = FREE_ACCESS_EMAILS.some(
    (e) => e.toLowerCase() === emailForFreeCheck.trim().toLowerCase()
  );

  // On mount: check if user already has an active subscription
  useEffect(() => {
    const checkExistingSubscription = async () => {
      try {
        const supabase = createClient();
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setCurrentUserId(user.id);
          const now = new Date().toISOString();
          const { data: sub } = await supabase
            .from('user_subscriptions')
            .select('id, expires_at, is_active')
            .eq('user_id', user.id)
            .eq('is_active', true)
            .gt('expires_at', now)
            .order('expires_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (sub) {
            setPaymentDone(true);
          }
        }
      } catch (err) {
        console.error('Subscription check error:', err);
      } finally {
        setCheckingSubscription(false);
      }
    };
    checkExistingSubscription();
  }, []);

  const handlePaymentSuccess = (planId: string) => {
    setPaymentDone(true);
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-white">
      {/* Razorpay Script */}
      <Script
        src="https://checkout.razorpay.com/v1/checkout.js"
        onLoad={() => setScriptLoaded(true)}
      />

      {/* Background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <img
          src="/assets/images/chennai_signin_bg.png"
          alt="Aerial view of Chennai city with Marina Beach at golden hour sunset"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.30) 50%, rgba(0,0,0,0.55) 100%)' }} />
      </div>

      {/* Decorative blurs */}
      <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-amber-300/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 right-10 w-48 h-48 rounded-full bg-blue-300/10 blur-3xl pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl px-4 py-8">
        {/* Logo + Branding */}
        <div className="flex flex-col items-center mb-6">
          <div className="flex items-center gap-4 mb-3">
            {/* Bus Icon */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" className="w-16 h-16 drop-shadow-xl">
              <rect x="6" y="14" width="52" height="32" rx="5" ry="5" fill="#F59E0B" />
              <rect x="8" y="10" width="48" height="8" rx="3" ry="3" fill="#D97706" />
              <rect x="42" y="17" width="12" height="10" rx="2" ry="2" fill="#BAE6FD" stroke="#7DD3FC" strokeWidth="1" />
              <rect x="10" y="17" width="9" height="9" rx="2" ry="2" fill="#BAE6FD" stroke="#7DD3FC" strokeWidth="1" />
              <rect x="22" y="17" width="9" height="9" rx="2" ry="2" fill="#BAE6FD" stroke="#7DD3FC" strokeWidth="1" />
              <rect x="34" y="17" width="6" height="9" rx="2" ry="2" fill="#BAE6FD" stroke="#7DD3FC" strokeWidth="1" />
              <rect x="10" y="29" width="8" height="12" rx="1" ry="1" fill="#B45309" />
              <line x1="14" y1="29" x2="14" y2="41" stroke="#92400E" strokeWidth="1" />
              <rect x="6" y="28" width="52" height="3" fill="#D97706" />
              <rect x="6" y="46" width="52" height="4" rx="1" fill="#92400E" />
              <circle cx="18" cy="52" r="6" fill="#1F2937" /><circle cx="18" cy="52" r="3" fill="#6B7280" /><circle cx="18" cy="52" r="1.2" fill="#D1D5DB" />
              <circle cx="46" cy="52" r="6" fill="#1F2937" /><circle cx="46" cy="52" r="3" fill="#6B7280" /><circle cx="46" cy="52" r="1.2" fill="#D1D5DB" />
              <rect x="54" y="30" width="4" height="5" rx="1" fill="#FEF3C7" />
              <rect x="8" y="10" width="30" height="5" rx="1" fill="#1F2937" />
              <text x="23" y="14.5" textAnchor="middle" fill="#FCD34D" fontSize="3.5" fontFamily="sans-serif" fontWeight="bold">CHENNAI MTC</text>
            </svg>
            {/* Train Icon */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" className="w-16 h-16 drop-shadow-xl">
              <rect x="10" y="10" width="44" height="36" rx="6" ry="6" fill="#2563EB" />
              <rect x="10" y="10" width="44" height="7" rx="6" ry="6" fill="#1D4ED8" />
              <rect x="10" y="14" width="44" height="3" fill="#1D4ED8" />
              <rect x="13" y="15" width="16" height="13" rx="3" ry="3" fill="#BFDBFE" stroke="#93C5FD" strokeWidth="1" />
              <rect x="35" y="15" width="16" height="13" rx="3" ry="3" fill="#BFDBFE" stroke="#93C5FD" strokeWidth="1" />
              <rect x="27" y="22" width="10" height="20" rx="1" fill="#1E40AF" />
              <line x1="32" y1="22" x2="32" y2="42" stroke="#1D4ED8" strokeWidth="1" />
              <rect x="10" y="28" width="7" height="14" rx="1" fill="#1E40AF" />
              <rect x="47" y="28" width="7" height="14" rx="1" fill="#1E40AF" />
              <rect x="10" y="42" width="44" height="4" rx="1" fill="#1E40AF" />
              <rect x="4" y="50" width="56" height="3" rx="1.5" fill="#374151" />
              <rect x="10" y="46" width="16" height="5" rx="1" fill="#374151" />
              <circle cx="14" cy="52" r="4.5" fill="#111827" /><circle cx="14" cy="52" r="2.5" fill="#4B5563" /><circle cx="14" cy="52" r="1" fill="#9CA3AF" />
              <circle cx="22" cy="52" r="4.5" fill="#111827" /><circle cx="22" cy="52" r="2.5" fill="#4B5563" /><circle cx="22" cy="52" r="1" fill="#9CA3AF" />
              <rect x="38" y="46" width="16" height="5" rx="1" fill="#374151" />
              <circle cx="42" cy="52" r="4.5" fill="#111827" /><circle cx="42" cy="52" r="2.5" fill="#4B5563" /><circle cx="42" cy="52" r="1" fill="#9CA3AF" />
              <circle cx="50" cy="52" r="4.5" fill="#111827" /><circle cx="50" cy="52" r="2.5" fill="#4B5563" /><circle cx="50" cy="52" r="1" fill="#9CA3AF" />
              <rect x="13" y="10" width="38" height="6" rx="2" fill="#0F172A" />
              <text x="32" y="14.5" textAnchor="middle" fill="#60A5FA" fontSize="3.5" fontFamily="sans-serif" fontWeight="bold">CHENNAI METRO</text>
              <circle cx="13" cy="38" r="2.5" fill="#FEF08A" />
              <circle cx="51" cy="38" r="2.5" fill="#FEF08A" />
            </svg>
          </div>
          <h1 className="text-3xl font-extrabold text-white text-center leading-tight drop-shadow-lg">Explore Ur Chennai</h1>
          <p className="text-base text-white/90 italic mt-1 text-center font-medium drop-shadow">Anywhere and Anytime</p>
        </div>

        {/* Two-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* LEFT — Subscription Plans */}
          <div className="bg-black/30 backdrop-blur-md rounded-2xl p-5 border border-white/20 shadow-2xl">
            <SubscriptionPanel
              selectedPlan={selectedPlan}
              onSelectPlan={setSelectedPlan}
              onPaymentSuccess={handlePaymentSuccess}
              scriptLoaded={scriptLoaded}
              paymentDone={paymentDone}
              userId={currentUserId}
            />
          </div>

          {/* RIGHT — Auth Card */}
          <div className="bg-white/90 backdrop-blur-md rounded-2xl p-6 shadow-2xl border border-white/60">
            {/* Tabs */}
            {tab !== 'forgot' && signupState === 'form' && (
              <div className="flex gap-1 p-1 rounded-xl bg-gray-100 mb-5">
                {(['signup', 'signin'] as AuthTab[]).map((t) => (
                  <button
                    key={`tab-${t}`}
                    onClick={() => { setTab(t); setSignupState('form'); }}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      tab === t ? 'bg-primary text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    {t === 'signin' ? 'Sign In' : 'Register (after choosing your plan)'}
                  </button>
                ))}
              </div>
            )}

            {/* Free email hint */}
            {!paymentDone && !isFreeEmail && tab !== 'forgot' && signupState === 'form' && (
              <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5">
                <p className="text-xs text-blue-700">
                  👈 <span className="font-semibold">Select a subscription plan</span> on the left and complete payment to unlock sign in.
                </p>
              </div>
            )}

            {/* Email watcher for free-access detection */}
            {tab !== 'forgot' && signupState === 'form' && !paymentDone && (
              <div className="mb-3">
                <label className="text-xs text-gray-500 font-medium block mb-1">Quick email check (for free access)</label>
                <input
                  type="email"
                  placeholder="Enter your email to check access..."
                  value={emailForFreeCheck}
                  onChange={(e) => setEmailForFreeCheck(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 text-xs rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
                />
                {isFreeEmail && (
                  <p className="text-xs text-green-600 font-semibold mt-1">✅ Free access granted for this email!</p>
                )}
              </div>
            )}

            {/* Form States */}
            {tab === 'forgot' && <ForgotForm onBack={() => setTab('signin')} />}
            {tab === 'signin' && (
              <SignInForm
                onForgot={() => setTab('forgot')}
                paymentDone={paymentDone}
                isFreeEmail={isFreeEmail}
              />
            )}
            {tab === 'signup' && signupState === 'form' && (
              <SignUpForm
                onSuccess={() => setSignupState('email-sent')}
                paymentDone={paymentDone}
                isFreeEmail={isFreeEmail}
              />
            )}
            {tab === 'signup' && signupState === 'email-sent' && (
              <EmailSentSuccess onBack={() => { setTab('signin'); setSignupState('form'); }} />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col items-center gap-2 mt-5">
          {/* Terms & Conditions */}
          <button
            onClick={() => setShowTerms(true)}
            className="text-white/70 text-xs font-medium hover:text-white transition-colors underline underline-offset-2 drop-shadow"
          >
            Terms &amp; Conditions
          </button>
          <a href="/" className="flex items-center gap-1.5 text-white/80 text-xs font-semibold hover:text-white transition-colors drop-shadow">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </a>
          <p className="text-center text-white/60 text-xs font-medium drop-shadow">© 2026 Explore Chennai · Explore the City</p>
        </div>
      </div>

      {/* Terms Modal */}
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
    </div>
  );
}
